/**
 * ApiKeysSection — Programmierzugänge (REST-API v1, MCP-Server, CLI).
 *
 * Warum es diese Seite gibt: Die Endpunkte existierten seit dem 09.08., aber es gab
 * keine Oberfläche dafür. Ein Kunde hätte sich seinen Schlüssel per curl ziehen müssen —
 * und, schlimmer, ein untergeschobener Schlüssel wäre für ihn unsichtbar geblieben.
 * Sichtbarkeit ist hier kein Komfort, sondern die Voraussetzung dafür, dass jemand
 * einen fremden Zugang überhaupt bemerken und widerrufen kann.
 *
 * Drei Dinge, die diese Komponente bewusst tut:
 *
 * 1. **Der Klartext steht genau einmal da.** Er kommt nur in der Antwort auf das
 *    Erzeugen; danach liegt in der Datenbank nur sein SHA-256. Die Anzeige sagt das
 *    ausdrücklich, statt den Nutzer glauben zu lassen, er könne ihn später nachschlagen.
 * 2. **Kein Klartext im Zustand über den Dialog hinaus.** Beim Schließen wird er
 *    verworfen; er landet nie in einer Liste, nie in localStorage.
 * 3. **Ehrlich bei 403.** Fehlt das Tarif-Merkmal, steht das als Satz da — nicht als
 *    leere Liste, die wie „du hast noch keine" aussieht.
 *
 * Der Zugang selbst hängt am Tarif (`api_access`, ab Wachstum). Durchgesetzt wird das
 * serverseitig; diese Seite zeigt es nur an.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, Plus, Trash2, Copy, Check, AlertTriangle, BookOpen } from "lucide-react";

const V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const SCOPE_TEXT = {
  read: "nur lesen",
  write: "lesen und Entwürfe anlegen",
  // Eigener Scope seit 10.08.2026. Vorher hing Freigeben und Einplanen mit an
  // "write" — die Zeile darüber versprach also "Entwürfe anlegen" und erlaubte
  // in Wahrheit, einen Beitrag freizugeben und zur Veröffentlichung einzuplanen.
  approve: "lesen, Entwürfe anlegen, freigeben und einplanen",
};

// Der Server liefert die Scopes alphabetisch sortiert: ["approve","read","write"].
// Aneinandergehängt las sich das als „… freigeben und einplanen · nur lesen · lesen und
// Entwürfe anlegen" — die scharfe Aussage stand vorn und wurde von zwei harmlosen
// überschrieben. Ein Schlüssel hat genau eine Stufe; die wird auch genau einmal genannt.
const STUFEN_REIHENFOLGE = ["approve", "write", "read"];

function hoechsteStufe(scopes) {
  const vorhanden = new Set(scopes || []);
  const treffer = STUFEN_REIHENFOLGE.find((s) => vorhanden.has(s));
  return treffer ? SCOPE_TEXT[treffer] : "keine Rechte";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ApiKeysSection() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gesperrt, setGesperrt] = useState(null); // Text, wenn der Tarif fehlt
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState("read");
  const [ablauf, setAblauf] = useState("90");
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${V1}/api-keys`);
      setKeys(res.data?.items || []);
      setGesperrt(null);
    } catch (e) {
      const fehler = e?.response?.data?.error;
      if (fehler?.code === "plan_feature_locked") {
        // Nicht als leere Liste zeigen — das sähe aus wie „du hast noch keine".
        setGesperrt(fehler.message);
      } else if (e?.response?.status !== 401) {
        toast.error(fehler?.message || "Zugänge konnten nicht geladen werden.");
      }
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Bitte einen Namen vergeben — daran erkennst du den Zugang später wieder.");
      return;
    }
    setCreating(true);
    try {
      const body = {
        name: name.trim(),
        scopes:
          scopes === "approve"
            ? ["read", "write", "approve"]
            : scopes === "write"
            ? ["read", "write"]
            : ["read"],
      };
      if (ablauf !== "nie") body.expires_in_days = Number(ablauf);
      const res = await axios.post(`${V1}/api-keys`, body);
      setSecret(res.data?.secret || null);
      setShowNew(false);
      setName("");
      await load();
    } catch (e) {
      const fehler = e?.response?.data?.error;
      if (fehler?.code === "plan_feature_locked") {
        // Die LISTE ist bewusst nicht tarifgesperrt — wer heruntergestuft wird, muss
        // seine Zugänge weiter sehen und widerrufen können. Erst beim Erzeugen greift
        // das Gate. Ohne diese Zeile bekäme der Kunde nur eine flüchtige Fehlermeldung
        // und stünde danach wieder vor demselben Knopf.
        setGesperrt(fehler.message);
        setShowNew(false);
      } else {
        toast.error(fehler?.message || "Zugang konnte nicht erstellt werden.");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    setRevoking(keyId);
    try {
      await axios.delete(`${V1}/api-keys/${keyId}`);
      toast.success(`Zugang „${keyName}" widerrufen — er funktioniert ab sofort nicht mehr.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Widerruf fehlgeschlagen.");
    } finally {
      setRevoking(null);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren nicht möglich — bitte den Schlüssel von Hand markieren.");
    }
  };

  const aktive = keys.filter((k) => k.active);

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4" data-testid="api-keys-section">
      <div className="flex items-center gap-2">
        <KeyRound size={20} className="text-stone-500" />
        <h2 className="font-heading font-bold text-lg text-stone-800">Programmierzugänge</h2>
      </div>
      <p className="text-sm text-stone-500">
        Mit einem Zugangsschlüssel kannst du deinen eigenen KI-Assistenten (Claude, ChatGPT
        oder ein eigenes Skript) auf deinen Workspace zugreifen lassen — Kalender lesen,
        Kennzahlen abrufen, Entwürfe anlegen.{" "}
        <strong>Was ein Schlüssel darf, entscheidest du je Schlüssel.</strong>{" "}
        Die beiden unteren Stufen können nichts veröffentlichen — Freigabe und Einplanung
        bleiben dann bei dir. Die oberste Stufe schließt beides ein.
      </p>

      {gesperrt && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="api-keys-locked">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Nicht in deinem Paket enthalten</p>
            <p className="mt-1">{gesperrt}</p>
            {keys.length > 0 && (
              <p className="mt-1">
                Bestehende Zugänge sind damit unwirksam. Du kannst sie unten trotzdem
                widerrufen.
              </p>
            )}
          </div>
        </div>
      )}

      {(
        <>
          {secret && (
            <div className="rounded-lg border border-green-300 bg-green-50 p-4 space-y-3" data-testid="api-key-secret-box">
              <p className="text-sm text-green-900 font-medium">
                Zugang erstellt. Diesen Schlüssel siehst du <strong>genau jetzt</strong> —
                danach nie wieder.
              </p>
              <p className="text-xs text-green-800">
                Wir speichern nur eine Prüfsumme davon. Wenn du ihn verlierst, erstellst du
                einfach einen neuen und widerrufst den alten.
              </p>
              <div className="flex gap-2">
                <code className="flex-1 px-3 py-2 rounded-lg bg-white border border-green-200 text-xs break-all font-mono">
                  {secret}
                </code>
                <button
                  onClick={copySecret}
                  data-testid="api-key-copy-btn"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 shrink-0"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? "Kopiert" : "Kopieren"}
                </button>
              </div>
              <button
                onClick={() => { setSecret(null); setCopied(false); }}
                data-testid="api-key-secret-close-btn"
                className="text-sm text-green-800 underline hover:no-underline"
              >
                Ich habe ihn gespeichert — ausblenden
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-stone-500">
              <Loader2 size={16} className="animate-spin" /> Zugänge werden geladen …
            </div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-stone-400" data-testid="api-keys-empty">
              Noch kein Zugang angelegt.
            </p>
          ) : (
            <div className="space-y-2" data-testid="api-keys-list">
              {keys.map((k) => (
                <div
                  key={k.key_id}
                  className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-stone-50 border border-stone-100"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-stone-800 truncate">{k.name}</span>
                      {!k.active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-200 text-stone-600">
                          {k.revoked_at ? "widerrufen" : "abgelaufen"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5 font-mono">{k.prefix}…</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {hoechsteStufe(k.scopes)}
                      {" · erstellt "}{formatDate(k.created_at)}
                      {k.expires_at ? ` · läuft ab ${formatDate(k.expires_at)}` : " · ohne Ablauf"}
                      {" · zuletzt genutzt "}{formatDate(k.last_used_at)}
                    </div>
                  </div>
                  {k.active && (
                    <button
                      onClick={() => handleRevoke(k.key_id, k.name)}
                      disabled={revoking === k.key_id}
                      data-testid={`api-key-revoke-${k.key_id}`}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60 shrink-0"
                    >
                      {revoking === k.key_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      Widerrufen
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {gesperrt ? null : showNew ? (
            <div className="rounded-lg border border-stone-200 p-4 space-y-3" data-testid="api-key-new-form">
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm text-stone-600 sm:col-span-3">
                  Wofür ist der Zugang?
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    placeholder="z. B. Mein Claude-Assistent"
                    data-testid="api-key-name-input"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-stone-400 outline-none text-sm"
                  />
                </label>
                <label className="text-sm text-stone-600">
                  Rechte
                  <select
                    value={scopes}
                    onChange={(e) => setScopes(e.target.value)}
                    data-testid="api-key-scope-select"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm bg-white"
                  >
                    <option value="read">Nur lesen (empfohlen)</option>
                    <option value="write">Lesen + Entwürfe anlegen</option>
                    <option value="approve">Lesen, Entwürfe, freigeben + einplanen</option>
                  </select>
                </label>
                <label className="text-sm text-stone-600">
                  Gültigkeit
                  <select
                    value={ablauf}
                    onChange={(e) => setAblauf(e.target.value)}
                    data-testid="api-key-expiry-select"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm bg-white"
                  >
                    <option value="30">30 Tage</option>
                    <option value="90">90 Tage (empfohlen)</option>
                    <option value="365">1 Jahr</option>
                    <option value="nie">Ohne Ablauf</option>
                  </select>
                </label>
              </div>
              {scopes !== "approve" ? (
                <p className="text-xs text-stone-500">
                  „Nur lesen" reicht für einen Assistenten, der beobachten und vorschlagen soll.
                  Erst „Entwürfe anlegen" lässt ihn etwas in deinen Plan schreiben — auch dann
                  geht nichts ohne deine Freigabe live.
                </p>
              ) : (
                <p className="text-xs text-amber-700" data-testid="api-key-approve-warning">
                  ⚠️ Diese Stufe ist etwas grundsätzlich anderes: Wer sie bekommt, darf Beiträge{" "}
                  <strong>selbst freigeben und zur Veröffentlichung einplanen</strong>.
                  Ab dem eingeplanten Termin geht der Beitrag ohne weitere Rückfrage live.
                  Gib sie nur einem Werkzeug, dem du das Veröffentlichen in deinem Namen
                  zutraust. Für einen KI-Assistenten reicht „Entwürfe anlegen".
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="api-key-create-submit"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-800 text-white text-sm font-medium hover:bg-stone-900 disabled:opacity-50"
                >
                  {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  Zugang erstellen
                </button>
                <button
                  onClick={() => { setShowNew(false); setName(""); }}
                  className="px-4 py-2 rounded-lg border border-stone-200 text-sm text-stone-600 hover:bg-stone-50"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowNew(true)}
                data-testid="api-key-new-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <Plus size={16} />
                Neuen Zugang erstellen
              </button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/docs`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 underline"
              >
                <BookOpen size={14} />
                Schnittstellen-Dokumentation
              </a>
              {aktive.length > 0 && (
                <span className="text-xs text-stone-400">
                  {aktive.length} aktiv{aktive.length === 1 ? "er" : "e"} Zugang
                  {aktive.length === 1 ? "" : "änge"}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
